import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/new-components/ui/popover";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../../components/new-components/ui/command";
import { closureWords } from "../providerApi";
import type { ProviderFacility } from "../providerApi";

/**
 * The provider's facility switcher: a searchable combobox over the whole list.
 *
 * A provider can cover ~48 buildings, so the plain dropdown it replaces (no
 * typing, a clipped list, first-letter typeahead only) hid most of them. Here
 * the search field takes focus on open, filters by name / city / state, and
 * the list scrolls, A-Z, with closed buildings last (still selectable) and the
 * last few buildings this browser opened on top.
 */

/** Only facility ids are kept, and only in this browser. */
export const RECENT_KEY = "fw:recent-facilities";

/**
 * The shell remounts the whole workspace when the facility changes, which drops the trigger the keyboard user was on.
 * Choosing a facility leaves a note here; the next trigger to mount takes focus.
 */
let focusTriggerOnMount = false;
const RECENT_KEPT = 6;
const RECENT_SHOWN = 3;
const PAGE = 8;

function readRecent(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((x): x is string => typeof x === "string").slice(0, RECENT_KEPT)
      : [];
  } catch {
    return [];
  }
}

function writeRecent(ids: string[]) {
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(ids.slice(0, RECENT_KEPT)));
  } catch {
    /* storage can be blocked or full: the picker works without it */
  }
}

function bumpRecent(ids: string[], id: string): string[] {
  return [id, ...ids.filter((x) => x !== id)].slice(0, RECENT_KEPT);
}

interface Row {
  id: string;
  name: string;
  place: string;
  closed: boolean;
  closedWords: string | null;
  haystack: string;
}

function toRow(f: Pick<ProviderFacility, "id" | "name" | "city" | "state" | "closure">): Row {
  const city = (f.city || "").trim();
  const state = (f.state || "").trim();
  const place = [city, state].filter(Boolean).join(", ");
  const name = f.name || "Name not recorded";
  return {
    id: f.id,
    name,
    place,
    closed: !!f.closure,
    closedWords: closureWords(f.closure),
    haystack: `${name} ${city} ${state} ${place}`.toLowerCase(),
  };
}

const byName = (a: Row, b: Row) =>
  a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }) ||
  a.id.localeCompare(b.id);

/** Every word typed must appear somewhere in the name, city or state. */
function matches(row: Row, query: string): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  return words.every((w) => row.haystack.includes(w));
}

export interface FacilityPickerProps {
  facilities: ProviderFacility[];
  /** The open facility (empty when none). */
  value: string;
  /** Said when the open facility is not in `facilities` (an enterprise search result). */
  fallbackName?: string;
  onChange: (id: string) => void;
  label?: string;
}

export default function FacilityPicker({
  facilities,
  value,
  fallbackName,
  onChange,
  label = "Facility",
}: FacilityPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState("");
  const [recent, setRecent] = useState<string[]>(readRecent);
  // The shadcn wrappers do not forward refs on React 18, so the panel is found through its own node.
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!focusTriggerOnMount) return;
    focusTriggerOnMount = false;
    triggerRef.current?.focus();
  }, []);
  const nameId = React.useId();
  const listId = React.useId();

  const rows = useMemo(() => {
    const all = facilities.map(toRow);
    if (value && !all.some((r) => r.id === value)) {
      all.push(toRow({ id: value, name: fallbackName || "Selected facility", city: null, state: null }));
    }
    return all;
  }, [facilities, value, fallbackName]);

  const current = rows.find((r) => r.id === value);

  // The buildings this browser opened, kept as ids only; the one open now counts too.
  useEffect(() => {
    if (!value || rows.length < 2) return;
    setRecent((prev) => {
      if (prev[0] === value) return prev;
      const next = bumpRecent(prev, value);
      writeRecent(next);
      return next;
    });
  }, [value, rows.length]);

  const open_ = useMemo(() => rows.filter((r) => !r.closed).sort(byName), [rows]);
  const closed_ = useMemo(() => rows.filter((r) => r.closed).sort(byName), [rows]);
  const filtering = query.trim() !== "";
  const shownOpen = filtering ? open_.filter((r) => matches(r, query)) : open_;
  const shownClosed = filtering ? closed_.filter((r) => matches(r, query)) : closed_;
  const shown = shownOpen.length + shownClosed.length;
  const recentRows = filtering
    ? []
    : recent
        .filter((id) => id !== value)
        .map((id) => rows.find((r) => r.id === id))
        .filter((r): r is Row => !!r)
        .slice(0, RECENT_SHOWN);

  const keyOf = (group: string, id: string) => `${group}:${id}`;
  // Rendering order, so PageUp / PageDown can step through the same sequence the eye follows.
  const order = [
    ...recentRows.map((r) => keyOf("recent", r.id)),
    ...shownOpen.map((r) => keyOf("all", r.id)),
    ...shownClosed.map((r) => keyOf("closed", r.id)),
  ];

  const pick = (id: string) => {
    setOpen(false);
    if (id === value) return;
    setRecent((prev) => {
      const next = bumpRecent(prev, id);
      writeRecent(next);
      return next;
    });
    focusTriggerOnMount = true;
    onChange(id);
  };

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      setRecent(readRecent());
      setQuery("");
      const here = rows.find((r) => r.id === value);
      setActive(here ? keyOf(here.closed ? "closed" : "all", here.id) : "");
    }
  };

  // Bring the open facility into view in a long list.
  useEffect(() => {
    if (!open || filtering) return;
    const t = window.setTimeout(() => {
      const el = panelRef.current?.querySelector<HTMLElement>('[data-fp-current="true"]');
      const list = el?.closest<HTMLElement>("[cmdk-list]");
      if (el && list) {
        // Only when the open facility is not already visible: otherwise the list stays at its top, so the
        // Recent heading and the first rows are not cut off on a phone.
        const lr = list.getBoundingClientRect();
        const er = el.getBoundingClientRect();
        if (er.top < lr.top || er.bottom > lr.bottom) {
          list.scrollTop += er.top - lr.top - (lr.height - er.height) / 2;
        }
      }
    }, 0);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // A single building needs no switcher, only its name.
  if (rows.length < 2) {
    const only = current || rows[0];
    return (
      <div className="fw-fp-single" title={only?.name} data-testid="fw-facility-single">
        <span className="fw-fp-name">{only?.name || "No facility"}</span>
        {only?.closed && <span className="fw-fp-tag">Closed</span>}
      </div>
    );
  }

  const renderRow = (r: Row, group: string) => {
    const isCurrent = r.id === value;
    const sub = [r.place, r.closed ? r.closedWords : null].filter(Boolean).join(" · ");
    return (
      <CommandItem
        key={keyOf(group, r.id)}
        value={keyOf(group, r.id)}
        onSelect={() => pick(r.id)}
        className="fw-fp-item"
        data-fp-current={isCurrent ? "true" : undefined}
      >
        <span className="fw-fp-item-text">
          <span className="fw-fp-item-name">{r.name}</span>
          {sub && <span className="fw-fp-item-sub">{sub}</span>}
        </span>
        {r.closed && <span className="fw-fp-tag">Closed</span>}
        {isCurrent && (
          <>
            <Check className="fw-fp-check" aria-hidden="true" />
            <span className="fw-sr-only">Current facility</span>
          </>
        )}
      </CommandItem>
    );
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          role="combobox"
          aria-label={label}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-describedby={nameId}
          title={current?.name}
          className="fw-select fw-fp-trigger"
          onKeyDown={(e) => {
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              onOpenChange(true);
            }
          }}
        >
          <span id={nameId} className="fw-fp-name">
            {current?.name || "Choose a facility"}
          </span>
          {current?.closed && <span className="fw-fp-tag">Closed</span>}
          <ChevronsUpDown className="fw-fp-chevron" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        collisionPadding={12}
        className="fw-facility-menu"
        aria-label="Choose a facility"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          panelRef.current?.querySelector<HTMLInputElement>("input")?.focus();
        }}
      >
        <div ref={panelRef} className="fw-fp-panel">
        <Command
          label="Search facilities"
          shouldFilter={false}
          loop
          value={active}
          onValueChange={setActive}
          onKeyDown={(e) => {
            if (e.key !== "PageDown" && e.key !== "PageUp") return;
            e.preventDefault();
            if (!order.length) return;
            const at = order.indexOf(active);
            const step = e.key === "PageDown" ? PAGE : -PAGE;
            const to = Math.min(order.length - 1, Math.max(0, (at < 0 ? 0 : at) + step));
            setActive(order[to]);
          }}
        >
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder="Search facilities…"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <div className="fw-fp-count" role="status" aria-live="polite">
            {filtering ? `${shown} of ${rows.length}` : `${rows.length} facilities`}
          </div>
          <CommandList id={listId} aria-label="Facilities" className="fw-fp-list">
            {shown === 0 && (
              <div className="fw-fp-empty">
                No facility matches “{query.trim()}”
              </div>
            )}
            {recentRows.length > 0 && (
              <CommandGroup heading="Recent">{recentRows.map((r) => renderRow(r, "recent"))}</CommandGroup>
            )}
            {shownOpen.length > 0 && (
              <CommandGroup heading={recentRows.length > 0 || shownClosed.length > 0 ? "All facilities" : undefined}>
                {shownOpen.map((r) => renderRow(r, "all"))}
              </CommandGroup>
            )}
            {shownClosed.length > 0 && (
              <CommandGroup heading="Closed buildings">
                {shownClosed.map((r) => renderRow(r, "closed"))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
        </div>
      </PopoverContent>
    </Popover>
  );
}
